import React, { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import Waveform from "@/components/Waveform";

/**
 * AudioPlayer Component
 * Reusable audio player with waveform visualization
 *
 * @param {string} audioUrl - URL of the audio file
 * @param {boolean} showStaticWaveform - Show static waveform when paused (default: true)
 * @param {function} onEnded - Callback when audio ends
 * @param {function} onClick - Callback when player is clicked (optional, for preventing navigation)
 */
const AudioPlayer = ({
	audioUrl,
	showStaticWaveform = true,
	onEnded,
	onClick,
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [audioLevels, setAudioLevels] = useState([]);

	const audioElementRef = useRef(null);
	const animationFrameRef = useRef(null);
	const realWaveformRef = useRef(null); // Static waveform data
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const audioBlobUrlRef = useRef(null);
	const progressBarRef = useRef(null);
	const progressFillRef = useRef(null);
	const thumbRef = useRef(null);

	// Load audio and extract real waveform
	useEffect(() => {
		const loadAudio = async () => {
			try {
				console.log("AudioPlayer: Loading audio from:", audioUrl);

				// Fetch and create blob URL for CORS-free playback
				const response = await fetch(audioUrl);
				const arrayBuffer = await response.arrayBuffer();

				const blob = new Blob([arrayBuffer], { type: "audio/webm" });
				const blobUrl = URL.createObjectURL(blob);
				audioBlobUrlRef.current = blobUrl;

				// Decode for static waveform
				const audioContext = new (
					window.AudioContext || window.webkitAudioContext
				)();
				const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

				const samples = 1024;
				const channelData = audioBuffer.getChannelData(0);
				const blockSize = Math.floor(channelData.length / samples);

				const waveform = [];
				for (let i = 0; i < samples; i++) {
					const sample = channelData[i * blockSize];
					waveform.push((sample + 1) * 128);
				}

				realWaveformRef.current = waveform;
				setAudioLevels(waveform);

				console.log("AudioPlayer: Waveform extracted successfully");
			} catch (error) {
				console.error("AudioPlayer: Failed to load audio:", error);
			}
		};

		if (audioUrl) {
			loadAudio();
		}

		return () => {
			if (audioBlobUrlRef.current) {
				URL.revokeObjectURL(audioBlobUrlRef.current);
			}
		};
	}, [audioUrl]);

	// Clean up on unmount
	useEffect(() => {
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, []);

	// Format time helper
	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Handle play/pause
	const togglePlay = (e) => {
		if (e) {
			e.stopPropagation();
		}

		const audioElement = audioElementRef.current;
		if (!audioElement) {
			return;
		}

		if (isPlaying) {
			// Pause
			audioElement.pause();
			setIsPlaying(false);
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			// Show static waveform
			if (showStaticWaveform && realWaveformRef.current) {
				setAudioLevels(realWaveformRef.current);
			}
		} else {
			// Play
			audioElement
				.play()
				.then(() => {
					setIsPlaying(true);

					// Start real-time visualization if analyser available
					if (analyserRef.current && !animationFrameRef.current) {
						// Resume audio context if suspended
						if (audioContextRef.current?.state === "suspended") {
							audioContextRef.current.resume();
						}

						const bufferLength = analyserRef.current.frequencyBinCount;
						const dataArray = new Uint8Array(bufferLength);

						const animate = () => {
							if (!audioElement || audioElement.paused) {
								return;
							}

							analyserRef.current.getByteTimeDomainData(dataArray);
							const waveformData = Array.from(dataArray);
							setAudioLevels(waveformData);

							animationFrameRef.current = requestAnimationFrame(animate);
						};

						animate();
					}
				})
				.catch((error) => {
					console.error("AudioPlayer: Error playing audio:", error);
				});
		}
	};

	// Handle seek
	const handleSeek = (e) => {
		if (!progressBarRef.current || !duration) {
			return;
		}

		e.stopPropagation();
		const rect = progressBarRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const percentage = x / rect.width;
		const newTime = percentage * duration;

		if (!isNaN(newTime) && audioElementRef.current) {
			audioElementRef.current.currentTime = newTime;
			setCurrentTime(newTime);
		}
	};

	return (
		<div className="w-full" onClick={onClick}>
			{/* Hidden audio element */}
			<audio
				ref={audioElementRef}
				src={audioBlobUrlRef.current || audioUrl}
				onLoadedMetadata={(e) => {
					setDuration(e.target.duration);

					// Set up Web Audio API for real-time visualization
					if (audioBlobUrlRef.current && !analyserRef.current) {
						try {
							const audioElement = e.target;
							audioContextRef.current = new (
								window.AudioContext || window.webkitAudioContext
							)();
							analyserRef.current = audioContextRef.current.createAnalyser();
							analyserRef.current.fftSize = 2048;
							analyserRef.current.smoothingTimeConstant = 0.8;

							const source =
								audioContextRef.current.createMediaElementSource(audioElement);
							source.connect(analyserRef.current);
							analyserRef.current.connect(audioContextRef.current.destination);

							console.log("AudioPlayer: Web Audio API set up successfully");
						} catch (error) {
							console.error("AudioPlayer: Failed to set up analyser:", error);
						}
					}
				}}
				onTimeUpdate={(e) => {
					setCurrentTime(e.target.currentTime);
				}}
				onEnded={() => {
					setIsPlaying(false);
					setCurrentTime(0);
					if (animationFrameRef.current) {
						cancelAnimationFrame(animationFrameRef.current);
						animationFrameRef.current = null;
					}
					if (showStaticWaveform && realWaveformRef.current) {
						setAudioLevels(realWaveformRef.current);
					}
					if (onEnded) {
						onEnded();
					}
				}}
				className="hidden"
			/>

			{/* Player UI */}
			<div
				className="w-full space-y-4 p-4"
				style={{
					backgroundColor: "rgba(0, 0, 0, 0.9)",
					borderColor: "rgba(234, 179, 8, 0.3)",
				}}
			>
				{/* Waveform */}
				<Waveform audioLevels={audioLevels} isRecording={false} />

				{/* Controls */}
				<div className="space-y-3">
					<div className="flex items-center gap-4">
						{/* Play/Pause Button */}
						<button
							onClick={togglePlay}
							className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-lg transition-all"
							style={{
								borderColor: "#fbbf24",
								backgroundColor: "#fbbf24",
								boxShadow: isPlaying
									? "0 0 20px rgba(251, 191, 36, 0.6)"
									: "0 0 10px rgba(251, 191, 36, 0.3)",
							}}
							aria-label={isPlaying ? "Pause audio" : "Play audio"}
						>
							{isPlaying ? (
								<Pause size={20} fill="white" color="white" />
							) : (
								<Play size={20} fill="white" color="white" />
							)}
						</button>

						{/* Progress Bar */}
						<div className="flex-1">
							<div
								ref={progressBarRef}
								className="group relative h-2 cursor-pointer rounded-full bg-gray-700"
								onClick={handleSeek}
							>
								{/* Progress Fill */}
								<div
									ref={progressFillRef}
									className="absolute left-0 top-0 h-full rounded-full"
									style={{
										width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
										backgroundColor: "#fbbf24",
										boxShadow: "0 0 10px rgba(251, 191, 36, 0.6)",
									}}
								/>

								{/* Draggable Thumb */}
								<div
									ref={thumbRef}
									className="absolute h-4 w-4 cursor-grab rounded-full border-2 border-white shadow-lg active:cursor-grabbing group-hover:scale-110"
									style={{
										left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
										top: "50%",
										backgroundColor: "#fbbf24",
										boxShadow: "0 0 10px rgba(251, 191, 36, 0.8)",
										transform: "translate(-50%, -50%)",
										pointerEvents: "auto",
									}}
									onMouseDown={(e) => {
										e.stopPropagation();
										e.preventDefault();

										const handleMouseMove = (moveEvent) => {
											if (!progressBarRef.current) {
												return;
											}

											const rect =
												progressBarRef.current.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(moveEvent.clientX - rect.left, rect.width)
											);
											const percentage = x / rect.width;
											const newTime = percentage * duration;

											if (!isNaN(newTime) && duration > 0) {
												// Direct DOM update for instant feedback
												const percentValue = `${percentage * 100}%`;
												if (progressFillRef.current) {
													progressFillRef.current.style.width = percentValue;
												}
												if (thumbRef.current) {
													thumbRef.current.style.left = percentValue;
												}
												if (audioElementRef.current) {
													audioElementRef.current.currentTime = newTime;
												}
											}
										};

										const handleMouseUp = () => {
											document.removeEventListener(
												"mousemove",
												handleMouseMove
											);
											document.removeEventListener("mouseup", handleMouseUp);
										};

										document.addEventListener("mousemove", handleMouseMove);
										document.addEventListener("mouseup", handleMouseUp);
									}}
									onTouchStart={(e) => {
										e.stopPropagation();
										e.preventDefault();

										const handleTouchMove = (moveEvent) => {
											moveEvent.preventDefault();
											if (!progressBarRef.current) {
												return;
											}

											const touch = moveEvent.touches[0];
											const rect =
												progressBarRef.current.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(touch.clientX - rect.left, rect.width)
											);
											const percentage = x / rect.width;
											const newTime = percentage * duration;

											if (!isNaN(newTime) && duration > 0) {
												// Direct DOM update for instant feedback
												const percentValue = `${percentage * 100}%`;
												if (progressFillRef.current) {
													progressFillRef.current.style.width = percentValue;
												}
												if (thumbRef.current) {
													thumbRef.current.style.left = percentValue;
												}
												if (audioElementRef.current) {
													audioElementRef.current.currentTime = newTime;
												}
											}
										};

										const handleTouchEnd = () => {
											document.removeEventListener(
												"touchmove",
												handleTouchMove
											);
											document.removeEventListener("touchend", handleTouchEnd);
										};

										document.addEventListener("touchmove", handleTouchMove, {
											passive: false,
										});
										document.addEventListener("touchend", handleTouchEnd);
									}}
								/>
							</div>
						</div>
					</div>

					{/* Duration Display */}
					<div
						className="flex w-full justify-between px-2 font-mono text-xs font-semibold"
						style={{ color: "#fbbf24" }}
					>
						<div>{formatTime(currentTime)}</div>
						<div>{formatTime(duration)}</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AudioPlayer;
